/**
 * Lecture d'un flux `text/event-stream` avec `fetch`.
 *
 * L'`EventSource` natif ne permet pas d'envoyer d'en-têtes ; or l'API IA exige
 * `X-Auth-Provider: reader-application` pour valider le jeton du reader.
 */
export interface SseMessage {
  event: string;
  data: string;
}

/** Découpe un flux SSE en messages. Les commentaires (`: keep-alive`) sont ignorés. */
export class SseParser {
  private buffer = "";
  private event = "";
  private data: string[] = [];

  push(chunk: string): SseMessage[] {
    this.buffer += chunk;
    const out: SseMessage[] = [];
    let newline: number;
    while ((newline = this.buffer.search(/\r\n|\r|\n/)) >= 0) {
      const line = this.buffer.slice(0, newline);
      const sepLength = this.buffer.startsWith("\r\n", newline) ? 2 : 1;
      this.buffer = this.buffer.slice(newline + sepLength);
      if (line === "") {
        if (this.data.length > 0 || this.event) {
          out.push({ event: this.event || "message", data: this.data.join("\n") });
        }
        this.event = "";
        this.data = [];
        continue;
      }
      if (line.startsWith(":")) continue;
      const colon = line.indexOf(":");
      const field = colon < 0 ? line : line.slice(0, colon);
      let value = colon < 0 ? "" : line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);
      if (field === "event") this.event = value;
      else if (field === "data") this.data.push(value);
    }
    return out;
  }
}

export class HttpStatusError extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${status}`);
    this.name = "HttpStatusError";
  }
}

/** Ouvre le flux et appelle `onMessage` pour chaque événement, jusqu'à la fin ou l'annulation. */
export async function readSse(
  fetchImpl: typeof fetch,
  url: string,
  headers: Record<string, string>,
  signal: AbortSignal,
  onMessage: (message: SseMessage) => void,
): Promise<void> {
  const response = await fetchImpl(url, { headers: { Accept: "text/event-stream", ...headers }, signal });
  if (!response.ok || !response.body) throw new HttpStatusError(response.status);
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  const parser = new SseParser();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    for (const message of parser.push(value)) onMessage(message);
  }
}
